// ─────────────────────────────────────────────────────────────────────────────
// LabThree — Guida "Digitalizzare i documenti HACCP"
// Centro assistenza → guida per amministratori.
//
// Schermate RICOSTRUITE (vedi la nota in atomi.js). Le etichette fra virgolette
// sono copiate dal codice: se una non corrisponde più a quello che si vede a
// schermo, va corretta qui.
//
// Riferimenti nel codice, per chi dovrà aggiornarla:
//   Schermata e form ......... App.js, sezione 'documenti' (CATEGORIE_DOC, BadgeStato)
//   Caricamento e limiti ..... backend/routes/documenti.js (multer, 20 MB, solo PDF)
//   Spazio per piano ......... backend/routes/documenti.js, costante LIMITI
//   Permessi ................. backend/routes/documenti.js (blindato il 17/08/2026)
//
// Il limite di 20 MB e' definito nella costante MAX_MB di documenti.js ed e'
// mostrato sotto il campo del file: se cambia, va allineato in tre punti —
// backend, form in App.js, e capitolo 2 di questa guida.
// ─────────────────────────────────────────────────────────────────────────────

import React from 'react';
import LT from '../../ui/tokens';
import I, { Ico } from '../../ui/icons';
import {
  GuidaLayout, H, P, Strong, Code, Tasto, Callout, Passo,
  MockFrame, Schermo, FAQ,
} from './atomi';

const SEZIONI = [
  { id: 'd0', label: 'Prima di iniziare' },
  { id: 'd1', label: '1. Cosa mettere dentro' },
  { id: 'd2', label: '2. Caricare un documento' },
  { id: 'd3', label: '3. I tre stati' },
  { id: 'd4', label: '4. Quando esce una revisione' },
  { id: 'd5', label: "5. Cosa vede l'operatore" },
  { id: 'd6', label: '6. Lo spazio disponibile' },
  { id: 'd7', label: '7. Eliminare' },
  { id: 'd8', label: 'Le trappole' },
  { id: 'd9', label: 'Domande frequenti' },
];

const CATEGORIE_DOC = [
  ['Piano HACCP', 'Il piano e i suoi allegati: analisi dei pericoli, albero delle decisioni, CCP.'],
  ['Procedure', 'Le procedure generali: sanificazione, lotta agli infestanti, gestione del vetro, richiamo.'],
  ['Istruzioni operative', 'Le istruzioni di dettaglio, quelle appese accanto alla macchina.'],
  ['Moduli', 'La modulistica in bianco, per quando serve la carta.'],
  ['Schede tecniche', 'Le schede dei vostri prodotti e quelle dei fornitori.'],
  ['Documenti tecnici', 'Planimetrie, schemi degli impianti, manuali delle attrezzature.'],
  ['Formazione', 'Attestati, registri delle sessioni, materiale usato.'],
  ['Certificazioni', 'Certificati aziendali, di prodotto, dei fornitori.'],
  ['Altro', 'Quello che non sta nelle altre otto.'],
];

const CampoFinto = ({ etichetta, valore, placeholder, larghezza }) => (
  <div style={{ marginBottom: 9, width: larghezza || '100%' }}>
    <div style={{
      fontSize: 10.5, fontWeight: 700, color: LT.n600,
      textTransform: 'uppercase', letterSpacing: 0.3, marginBottom: 3,
    }}>{etichetta}</div>
    <div style={{
      border: `1px solid ${LT.n300}`, borderRadius: LT.r.md, padding: '7px 9px',
      background: LT.n0, fontSize: 13,
      color: valore ? LT.n900 : LT.n400,
    }}>{valore || placeholder}</div>
  </div>
);

const Badge = ({ testo, kind }) => {
  const c = {
    bozza:      { bg: LT.arancioSoft, fg: '#92400E', bd: '#FDE9B5' },
    vigore:     { bg: LT.verdeSoft,   fg: '#166534', bd: '#C7EFDA' },
    archiviato: { bg: LT.n100,        fg: LT.n600,   bd: LT.n200 },
  }[kind];
  return (
    <span style={{
      padding: '3px 9px', borderRadius: 999, fontSize: 11, fontWeight: 700,
      background: c.bg, color: c.fg, border: `1px solid ${c.bd}`, whiteSpace: 'nowrap',
    }}>{testo}</span>
  );
};

export default function DocumentiHaccp() {
  return (
    <GuidaLayout
      titolo="Digitalizzare i documenti HACCP"
      sottotitolo="Portare dentro LabThree il manuale, le procedure e i piani, e tenerne sotto controllo le revisioni. Da qui l'operatore li consulta dal telefono, e all'ispezione sono tutti in un posto solo."
      durata="~ 12 minuti"
      destinatario="Per amministratori"
      sezioni={SEZIONI}
    >

      {/* d0 */}
      <H id="d0">Prima di iniziare</H>

      <P>
        La sezione <Code>Documenti</Code> è l'archivio dei documenti scritti della vostra azienda:
        il manuale HACCP, le procedure, le planimetrie, gli attestati di formazione. Non li
        genera LabThree e non li controlla: li conserva, li tiene ordinati e li rende consultabili
        a chi lavora, anche dal telefono.
      </P>
      <P>
        Serve a tre cose concrete. L'operatore che non ricorda una procedura la trova da solo
        invece di chiamarvi. All'ispezione non cercate più in tre cartelle diverse. E soprattutto
        si tiene traccia di <Strong>quale versione è quella in vigore</Strong>, che è un requisito
        del sistema HACCP e la cosa che in azienda si perde per prima.
      </P>

      <Callout kind="info" titolo="Cosa ti serve avere pronto">
        I documenti in <Strong>PDF</Strong>. È l'unico formato accettato: un Word va convertito
        prima, e la carta va scansionata. Se scansioni, tieni la risoluzione bassa — 150 dpi in
        bianco e nero bastano per un documento di testo e pesano molto meno.
      </Callout>

      <P>
        Non serve fare tutto in una volta. Se dovete partire da zero, l'ordine più utile è: prima
        il manuale HACCP e il piano, poi le procedure che l'operatore consulta davvero, e in
        fondo l'archivio storico. I documenti che nessuno apre possono aspettare.
      </P>

      {/* d1 */}
      <H id="d1" cat="formazione">1. Cosa mettere dentro</H>

      <P>
        Ogni documento va messo in una delle nove categorie. Non si possono aggiungere o
        rinominare, quindi vale la pena sapere a cosa servono per non ritrovarsi tutto sotto
        "Altro".
      </P>

      <div style={{ margin: '18px 0' }}>
        {CATEGORIE_DOC.map(([nome, cosa]) => (
          <div key={nome} style={{
            display: 'flex', gap: 12, alignItems: 'flex-start',
            padding: '9px 0', borderBottom: `1px solid ${LT.n150}`,
          }}>
            <div style={{
              fontSize: 13, fontWeight: 700, color: LT.navy,
              width: 155, flexShrink: 0,
            }}>{nome}</div>
            <div style={{ fontSize: 13.5, color: LT.n600, lineHeight: 1.5 }}>{cosa}</div>
          </div>
        ))}
      </div>

      <Callout kind="tip" titolo="Il nome conta più della categoria">
        La categoria serve a raggruppare, ma quello che si legge nell'elenco è il nome. Scrivi
        nomi che un operatore riconosce: <Code>Procedura di sanificazione</Code> funziona,{' '}
        <Code>PRC-07 rev.3</Code> no. Il codice interno, se ce l'avete, va nel campo Revisione.
      </Callout>

      {/* d2 */}
      <H id="d2">2. Caricare un documento</H>

      <P>
        Dalla sezione <Code>Documenti</Code> premi <Tasto>Nuovo documento</Tasto>. Sono cinque
        campi, di cui quattro obbligatori.
      </P>

      <MockFrame markers={[
        { n: 1, top: '26%', left: '50%', titolo: 'Nome',
          testo: 'È quello che l\'operatore legge nell\'elenco. Chiaro e in italiano corrente.' },
        { n: 2, top: '58%', left: '50%', titolo: 'Revisione',
          testo: 'Facoltativo ma decisivo: è il campo che permette di capire quale versione si sta guardando.' },
        { n: 3, top: '75%', left: '50%', titolo: 'Stato',
          testo: 'Parte da "Bozza". Finché resta così, l\'operatore non lo vede. Vedi il capitolo 3.' },
      ]}>
        <Schermo style={{ padding: 20, background: LT.n100 }}>
          <div style={{
            maxWidth: 360, margin: '0 auto', background: LT.n0,
            borderRadius: LT.r.lg, border: `1px solid ${LT.n200}`, padding: 16,
            boxShadow: LT.sh2,
          }}>
            <div style={{ fontSize: 16.5, fontWeight: 800, color: LT.navy, marginBottom: 13 }}>
              Nuovo documento
            </div>
            <CampoFinto etichetta="Nome *" placeholder="es. Piano HACCP Stabilimento A" />
            <CampoFinto etichetta="Categoria *" placeholder="Seleziona..." />
            <CampoFinto etichetta="Revisione" placeholder="es. Ed.2 Rev. 01" />
            <CampoFinto etichetta="Stato *" valore="Bozza" />
            <CampoFinto etichetta="Descrizione" placeholder="Descrizione opzionale..." />
            <CampoFinto etichetta="File PDF *" placeholder="Scegli file" />
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 7, marginTop: 12 }}>
              <span style={{
                padding: '7px 13px', borderRadius: LT.r.md, fontSize: 12.5, fontWeight: 600,
                background: LT.n0, color: LT.n700, border: `1px solid ${LT.n300}`,
              }}>Annulla</span>
              <span style={{
                padding: '7px 14px', borderRadius: LT.r.md, fontSize: 12.5, fontWeight: 700,
                background: LT.blu, color: '#fff',
              }}>Salva</span>
            </div>
          </div>
        </Schermo>
      </MockFrame>

      <P>
        Se manca qualcosa il salvataggio si ferma con un messaggio preciso —{' '}
        <Strong>"Il nome è obbligatorio"</Strong>, <Strong>"Seleziona una categoria"</Strong>,{' '}
        <Strong>"Seleziona un file PDF"</Strong> — quindi non c'è modo di caricare un documento
        a metà.
      </P>

      <Callout kind="info" titolo="Quanto può pesare">
        Il limite è di <Strong>20 MB per file</Strong>, ed è indicato anche sotto il campo. Per un
        documento di testo è abbondante: si arriva a superarlo solo con le scansioni a colori ad
        alta risoluzione. Se devi scansionare della carta, 150 dpi in bianco e nero danno un PDF
        leggero e perfettamente leggibile.
      </Callout>

      {/* d3 */}
      <H id="d3" cat="temperature">3. I tre stati</H>

      <P>
        Ogni documento ha uno stato, e questa è la parte che trasforma un archivio in un sistema
        di controllo dei documenti. Sono tre.
      </P>

      <div style={{ margin: '18px 0' }}>
        {[
          ['bozza', 'Bozza',
            'Sta in cassaforte: lo vedi solo tu e gli altri amministratori. L\'operatore non sa nemmeno che esiste. È lo stato in cui si lavora a un documento non ancora approvato.'],
          ['vigore', 'In vigore',
            'È la versione valida, quella che tutti devono seguire. È anche l\'unico stato che l\'operatore vede.'],
          ['archiviato', 'Archiviato',
            'Non è più valido ma resta conservato. Serve quando esce una revisione: la vecchia si archivia invece di buttarla.'],
        ].map(([kind, nome, cosa]) => (
          <div key={kind} style={{
            display: 'flex', gap: 12, alignItems: 'flex-start',
            padding: '11px 0', borderBottom: `1px solid ${LT.n150}`,
          }}>
            <div style={{ width: 92, flexShrink: 0, paddingTop: 1 }}>
              <Badge testo={nome} kind={kind} />
            </div>
            <div style={{ fontSize: 13.5, color: LT.n600, lineHeight: 1.55 }}>{cosa}</div>
          </div>
        ))}
      </div>

      <P>
        La regola che conta, e che vale la pena ripetere a voce a chi vi aiuta a caricare i
        documenti: <Strong>l'operatore vede soltanto quelli "In vigore"</Strong>. Un documento
        rimasto in Bozza per distrazione, per lui non esiste. È la spiegazione del novanta per
        cento delle volte in cui qualcuno dice "non trovo la procedura".
      </P>

      {/* d4 */}
      <H id="d4">4. Quando esce una revisione</H>

      <P>
        Qui c'è la scelta più importante di tutta questa guida, e il programma vi lascia liberi di
        sbagliarla. Quando un documento cambia — nuova edizione del manuale, procedura aggiornata —
        avete due strade.
      </P>

      <Passo n={1} titolo="La strada sbagliata: sostituire il file">
        <P style={{ margin: '6px 0' }}>
          Aprire il documento esistente, premere <Tasto>Modifica</Tasto> e caricare il PDF nuovo.
          Funziona, ed è quello che viene istintivo. Ma il file precedente{' '}
          <Strong>viene cancellato dal server</Strong>, e della versione vecchia non resta niente.
          Se un giorno un ispettore vi chiede quale procedura era in vigore l'anno scorso, non
          avete come rispondere.
        </P>
      </Passo>

      <Passo n={2} titolo="La strada giusta: archiviare e ricaricare">
        <P style={{ margin: '6px 0' }}>
          Mettere il documento vecchio in stato <Strong>Archiviato</Strong>, poi caricare quello
          nuovo come documento a sé, con la revisione aggiornata nel campo apposito. In elenco
          restano entrambi: uno "In vigore" e uno "Archiviato". L'operatore vede solo il primo,
          e voi conservate la storia.
        </P>
      </Passo>

      <Callout kind="warn" titolo="Usa Modifica solo per correggere">
        <Tasto>Modifica</Tasto> va bene per aggiustare il nome, la categoria, la descrizione, o
        per rimpiazzare un file caricato per errore cinque minuti prima. Non per far avanzare una
        revisione: quella si archivia.
      </Callout>

      <P>
        Il campo <Strong>Revisione</Strong> è il posto dove scrivere l'identificativo che usate
        voi: <Code>Ed.2 Rev. 01</Code>, <Code>Rev. 3 del 12/03/2026</Code>, o il codice del
        vostro consulente. Non serve al programma, serve a chi legge l'elenco e deve capire in un
        colpo d'occhio quale delle due copie è quella buona.
      </P>

      {/* d5 */}
      <H id="d5">5. Cosa vede l'operatore</H>

      <P>
        La stessa sezione, ma molto più semplice. In cima, al posto di "Gestione documenti
        aziendali", legge <Strong>"Consultazione documenti in vigore"</Strong>, che dice già tutto.
      </P>
      <P>
        Vede solo i documenti in vigore. Non vede i badge di stato, perché per lui esiste un solo
        stato. Non ha il filtro per stato, non ha <Tasto>Nuovo documento</Tasto>, e su ogni
        documento ha un solo pulsante: <Tasto>Apri</Tasto>. Non può caricare, modificare o
        eliminare niente, e questo non dipende dai pulsanti nascosti: è il server che rifiuta.
      </P>

      <Callout kind="tip" titolo="Provalo con i suoi occhi">
        Prima di dire ai vostri che i documenti sono online, entrate una volta con un PIN da
        operatore e guardate l'elenco. È l'unico modo per accorgersi di quello che è rimasto in
        Bozza.
      </Callout>

      {/* d6 */}
      <H id="d6">6. Lo spazio disponibile</H>

      <P>
        In alto nella sezione c'è una barra <Strong>"Spazio utilizzato"</Strong> con il vostro
        piano. Lo spazio è condiviso fra i documenti HACCP, gli allegati delle attività e quelli
        delle non conformità — non è solo dei PDF di questa sezione.
      </P>
      <P>
        Il piano START ha 1 GB, TRACE 5 GB. Per dei documenti di testo è tantissimo: si arriva a
        riempirlo solo caricando scansioni a colori ad alta risoluzione, che è un altro buon
        motivo per scansionare leggeri. Oltre il 90% compare l'avviso{' '}
        <Strong>"Spazio quasi esaurito — contatta il supporto per aggiornare il piano."</Strong>
      </P>

      {/* d7 */}
      <H id="d7">7. Eliminare</H>

      <P>
        <Tasto>Elimina</Tasto> chiede conferma e lo dice chiaramente: "Il file PDF verrà rimosso
        dal server. L'operazione non è reversibile." Non è un modo di dire — il file viene
        cancellato dal disco e non c'è cestino.
      </P>

      <Callout kind="warn" titolo="Nel dubbio, archivia">
        Per un documento superato non serve eliminare: <Strong>Archiviato</Strong> lo toglie dalla
        vista dell'operatore e lo conserva. L'eliminazione ha senso solo per un file caricato per
        sbaglio o per un duplicato. Un documento HACCP superato è comunque una prova di come
        lavoravate: buttarlo è una perdita, non una pulizia.
      </Callout>

      {/* d8 */}
      <H id="d8">Le trappole</H>

      <div style={{ margin: '16px 0' }}>
        {[
          ['"L\'operatore non trova il documento"',
            'Quasi sempre è rimasto in stato Bozza. L\'operatore vede solo quelli In vigore.'],
          ['Sostituire il file perde la versione precedente',
            'Modifica con un nuovo PDF cancella il vecchio dal server. Per una revisione, archivia il documento vecchio e carica il nuovo a parte.'],
          ['Lo spazio del piano è condiviso',
            'Il GB del piano non è soltanto dei documenti: comprende anche gli allegati delle attività e delle non conformità.'],
          ['Si carica solo PDF',
            'Word, Excel e le foto non vengono accettati in questa sezione. Vanno convertiti in PDF prima.'],
          ['Eliminare è definitivo',
            'Nessun cestino, il file esce dal disco. Per i documenti superati usa Archiviato.'],
        ].map(([t, d]) => (
          <div key={t} style={{
            display: 'flex', gap: 11, alignItems: 'flex-start',
            padding: '11px 0', borderBottom: `1px solid ${LT.n150}`,
          }}>
            <Ico d={I.alert} size={16} stroke={LT.arancio} style={{ marginTop: 3, flexShrink: 0 }} />
            <div>
              <div style={{ fontSize: 14.5, fontWeight: 700, color: LT.n900 }}>{t}</div>
              <div style={{ fontSize: 13.5, color: LT.n600, lineHeight: 1.55, marginTop: 2 }}>{d}</div>
            </div>
          </div>
        ))}
      </div>

      {/* d9 */}
      <H id="d9">Domande frequenti</H>

      <FAQ q="Devo caricare tutto il manuale HACCP o pezzo per pezzo?">
        Meglio pezzo per pezzo, seguendo le categorie. Un unico PDF da duecento pagine è
        formalmente completo e praticamente inutile: l'operatore che cerca la procedura di
        sanificazione non la trova. Caricate il piano come "Piano HACCP" e ogni procedura come
        documento a sé sotto "Procedure".
      </FAQ>

      <FAQ q="LabThree sostituisce il mio manuale HACCP?">
        No, e nessun software può farlo. Il manuale lo redige chi ne ha la competenza — voi con il
        vostro consulente — e resta un documento vostro. LabThree lo conserva, lo distribuite a
        chi lavora e ne tiene sotto controllo le revisioni.
      </FAQ>

      <FAQ q="Posso caricare un documento che vale per più stabilimenti?">
        Sì, ma tieni presente che l'archivio è per azienda: se in LabThree avete più aziende
        separate, il documento va caricato in ognuna. Metti nel nome a quale stabilimento si
        riferisce, come suggerisce il testo di esempio del campo.
      </FAQ>

      <FAQ q="Come faccio a far firmare a un operatore che ha letto una procedura?">
        Questa sezione conserva e distribuisce i documenti, non raccoglie le prese visione. Se
        vi serve la prova che una persona ha letto e capito qualcosa, lo strumento è lo
        Scadenziario: un'attività di presa visione assegnata a quella persona lascia una
        registrazione col suo nome e la data.
      </FAQ>

      <FAQ q="Un operatore può eliminare un documento per sbaglio?">
        No. Caricare, modificare ed eliminare sono operazioni riservate agli amministratori, e il
        controllo è sul server: non dipende dal fatto che i pulsanti non gli compaiano.
      </FAQ>

      <div style={{
        marginTop: 40, padding: 18, background: LT.bluSoft,
        borderRadius: LT.r.lg, border: `1px solid ${LT.n150}`,
      }}>
        <div style={{ fontSize: 15.5, fontWeight: 700, color: LT.navy, marginBottom: 4 }}>
          E adesso?
        </div>
        <div style={{ fontSize: 14, color: LT.n700, lineHeight: 1.65 }}>
          Se hai caricato i primi documenti, la cosa che vale mezz'ora è passarli tutti in rassegna
          e controllare due colonne: che quelli che l'operatore deve consultare siano{' '}
          <Strong>In vigore</Strong>, e che nessuna revisione superata sia rimasta lì insieme a
          quella nuova.
        </div>
      </div>

    </GuidaLayout>
  );
}
